import axios from 'axios';
import {
  Box,
  Button,
  ButtonGroup,
  Checkbox,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import React, {
  Dispatch,
  ChangeEvent,
  useEffect,
  useRef,
  useState,
  SyntheticEvent,
} from 'react';
import { QueryCache, useMutation, useQuery, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { useRecoilState } from 'recoil';
import clsx from 'clsx';
import { startOfYear } from 'date-fns';

import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import FileDownloadRoundedIcon from '@mui/icons-material/FileDownloadRounded';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import SearchIcon from '@mui/icons-material/Search';
import { useSession, xapi } from '../../hooks/session';
import { SwSnackbar } from '../commonComponent/views/SwSnackbar';
import SwLoading from '../commonComponent/spinner/SwLoading';
import { SwPagination } from '../commonComponent/tableElement/SwPagination';
import {
  commaFormat,
  encodeSearchWord,
  getGender,
  timestamp2Localestring,
  TOOLTIP_FONT_SIZE,
} from '../../common/helper';
import {
  KEY_BOOK_GROUP_LIST,
  KEY_BOOK_INFO_LIST,
  KEY_CLASS_USER_STAT_LIST,
  KEY_EDUCATION_OFFICE_SCHOOL_STAT_LIST,
  KEY_SCHOOL_CLASS_STAT_LIST,
} from '../../common/key';
import excellog from '../../images/sub/excel_icon.svg';
import {
  getBookGroupList,
  getBookInfoList,
  registerBookGroup,
} from '../../common/api/ApiBook';
import { SwBookGroup } from '../commonComponent/SwBookGroup';
import { SwBookList } from '../commonComponent/SwBookList';
import { uploadFile } from '../../common/api/ApiUpload';
import { RESULT_OK } from '../../common/resultCode';
import { SwClassStatTab } from '../commonComponent/SwClassStatTab';
import { SwUserStatDetail } from '../commonComponent/SwUserStatDetail';
import {
  deleteMemberSchoolClass,
  getClassUserStatList,
} from '../../common/api/ApiClassStat';
import { SwConfirmDialog } from '../commonComponent/views/SwConfirmDialog';
import { selectSchoolInfoState } from '../../common/atom';
import { SwDateRangePicker } from '../commonComponent/dateSetting/SwDateRangePicker';
import { SwSchoolStatTab } from '../commonComponent/SwSchoolStatTab';
import { getSchoolStatClassList } from '../../common/api/ApiSchool';
import { SwClassSelector } from '../commonComponent/SwClassSelector';
import { SwEducationOfficeStatTab } from '../commonComponent/SwEducationOfficeStatTab';
import { SwSchoolSelector } from '../commonComponent/SwSchoolSelector';
import { getEducationOfficeSchoolList } from '../../common/api/ApiEducationOffice';

const columns = [
  '지원청명',
  '학교',
  '사용자 수',
  '완독 권수',
  '누적 걸음수',
  '작성 글 수',
];

interface Props {}

export const EducationOfficeSchoolStat: React.FC<Props> = () => {
  const [schoolClassInfo, setSchoolClassInfo] = useRecoilState(
    selectSchoolInfoState
  );
  const [educationOfficeInfo, setEducationOfficeInfo] = useState<any>();
  const { loginVal } = useSession();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [requestList, setRequestList] = useState<any>();
  const [selectedSeq, setSelectedSeq] = useState<number>(-1);
  const [selectSchool, setSelectSchool] = useState<any>({
    schoolGroupSeq: 0,
    schoolSeq: 0,
  });
  const [rangeDate, setRangeDate] = useState([
    startOfYear(new Date()), // 올해 1월 1일
    new Date(), // 오늘
  ]);

  const [arrowGrade, setArrowGrade] = useState<number>(0);
  const [arrowClass, setArrowClass] = useState<number>(0);
  const [arrowMember, setArrowMember] = useState<number>(-1);
  const [arrowFinish, setArrowFinish] = useState<number>(-1);
  const [arrowTotal, setArrowTotal] = useState<number>(-1);
  const [arrowWrite, setArrowWrite] = useState<number>(-1);
  const rowsPerPageOptions = [5, 10, 15, 20];
  const [page, setPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchKey, setSearchKey] = useState<string>('nickname');
  const [searchWord, setSearchWord] = useState<string>('');
  const [dailyStatType, setDailyStatType] = useState<any>(0);
  const [isToday, setIsToday] = useState<any>(false);
  const [loading, setLoading] = useState<any>(false);
  const [downloadType, setDownloadType] = useState<any>('');
  const [dialogMessage, setDialogMessage] = useState<string>('');
  const [openSnackbar, setOpenSnackbar] = useState<boolean>(false);
  const [openConfirm, setOpenConfirm] = useState<boolean>(false);
  const [openUserStat, setOpenUserStat] = useState<boolean>(false);

  const {
    data,
    isLoading,
    refetch: refetchEducationOfficeSchoolStat,
  } = useQuery(
    KEY_EDUCATION_OFFICE_SCHOOL_STAT_LIST,
    () => {
      if (schoolClassInfo !== undefined && schoolClassInfo !== null) {
        const newData: any = {
          educationOfficeSeq:
            loginVal.value.user.roleName === '마스터'
              ? 0
              : schoolClassInfo.educationOfficeSeq,
          schoolGroupSeq: selectSchool.schoolGroupSeq,
          schoolSeq: selectSchool.schoolSeq,
          startDate: rangeDate[0] === null ? 0 : rangeDate[0].getTime(),
          endDate: rangeDate[1] === null ? 0 : rangeDate[1].getTime(),
          page: page,
          rowsPerPage: rowsPerPage,
          searchKey: searchKey,
          searchWord: encodeSearchWord(searchKey, searchWord),
          sortByMember: arrowMember,
          sortByFinish: arrowFinish,
          sortByTotal: arrowTotal,
          sortByWrite: arrowWrite,
        };
        return getEducationOfficeSchoolList(newData);
      }
      return null;
    },
    {
      onSuccess: res => {
        setLoading(false);
        if (res !== null) {
          setRequestList(res.statList);
          setTotalRecords(res.totalRecordCount);
        } else {
          requestList([]);
        }
      },
      onError: e => {
        setLoading(false);
        console.log(e);
      },
    }
  );

  const prefetchEducationOfficeSchoolStat = (
    newSchoolGroupSeq: number,
    newSchoolSeq: number
  ) => {
    queryClient.prefetchQuery(KEY_EDUCATION_OFFICE_SCHOOL_STAT_LIST, () => {
      if (schoolClassInfo !== null) {
        const newData: any = {
          educationOfficeSeq:
            loginVal.value.user.roleName === '마스터'
              ? 0
              : schoolClassInfo.educationOfficeSeq,
          schoolGroupSeq: newSchoolGroupSeq,
          schoolSeq: newSchoolSeq,
          startDate: rangeDate[0] === null ? 0 : rangeDate[0].getTime(),
          endDate: rangeDate[1] === null ? 0 : rangeDate[1].getTime(),
          page: page,
          rowsPerPage: rowsPerPage,
          searchKey: searchKey,
          searchWord: encodeSearchWord(searchKey, searchWord),
          sortByMember: arrowMember,
          sortByFinish: arrowFinish,
          sortByTotal: arrowTotal,
          sortByWrite: arrowWrite,
        };
        return getEducationOfficeSchoolList(newData);
      }
      return null;
    });
  };

  const downloadSchoolClassStat = (param: any) =>
    xapi
      .get(
        `/admin/v1/apis/education-office-stat/school/download?seq=${param.educationOfficeSeq}&schoolGroupSeq=${param.schoolGroupSeq}&schoolSeq=${param.schoolSeq}&page=${param.page}&perPage=${param.rowsPerPage}&searchKey=${param.searchKey}&searchWord=${param.searchWord}&startDate=${param.startDate}&endDate=${param.endDate}&sortByGrade=${param.sortByGrade}&sortByClass=${param.sortByClass}&sortByMember=${param.sortByMember}&sortByFinish=${param.sortByFinish}&sortByAvg=${param.sortByAvg}&sortByTotal=${param.sortByTotal}&sortByWrite=${param.sortByWrite}`,
        { responseType: 'blob' }
      )
      .then(res => {
        const blob = new Blob([res.data]);
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `seducation-office-school-stat.xlsx`;
        document.body.appendChild(a);
        a.click();
        setTimeout((_: any) => {
          window.URL.revokeObjectURL(url);
        }, 600000);
        a.remove();
        setLoading(false);
      })
      .catch(err => {
        setLoading(false);
        console.error('err: ', err);
      });

  const downloadClassStatUserList = () => {
    const newData = {
      educationOfficeSeq:
        loginVal.value.user.roleName === '마스터'
          ? 0
          : schoolClassInfo.educationOfficeSeq,
      schoolGroupSeq: selectSchool.schoolGroupSeq,
      schoolSeq: selectSchool.schoolSeq,
      startDate: rangeDate[0] === null ? 0 : rangeDate[0].getTime(),
      endDate: rangeDate[1] === null ? 0 : rangeDate[1].getTime(),
      page: 0,
      rowsPerPage: 0,
      searchKey: searchKey,
      searchWord: encodeSearchWord(searchKey, searchWord),
      sortByMember: arrowMember,
      sortByFinish: arrowFinish,
      sortByTotal: arrowTotal,
      sortByWrite: arrowWrite,
    };
    setLoading(true);
    downloadSchoolClassStat(newData);
  };

  const handleChangePage = (_event: any, newPage: number) => {
    setPage(newPage);
  };

  const searchClassStatUsers = () => {
    if (page === 1) {
      setLoading(true);
      refetchEducationOfficeSchoolStat();
    } else {
      setPage(1);
    }
  };

  const handleKeyDown = (e: any) => {
    if (e.key === 'Enter') {
      searchClassStatUsers();
    }
  };

  const detailUserStat = (idx: number) => {
    const newSeq = requestList[idx].seq;
    setSelectedSeq(newSeq);
    setOpenUserStat(true);
  };

  const onClickArrowButton = (idx: number) => {
    setArrowMember(
      idx === 2 && arrowMember !== 1
        ? 1
        : idx === 2 && arrowMember === 1
        ? 0
        : -1
    );
    setArrowFinish(
      idx === 3 && arrowFinish !== 1
        ? 1
        : idx === 3 && arrowFinish === 1
        ? 0
        : -1
    );
    setArrowTotal(
      idx === 4 && arrowTotal !== 1 ? 1 : idx === 4 && arrowTotal === 1 ? 0 : -1
    );
    setArrowWrite(
      idx === 5 && arrowWrite !== 1 ? 1 : idx === 5 && arrowWrite === 1 ? 0 : -1
    );
  };

  useEffect(() => {
    if (selectSchool.schoolGroupSeq !== 0) {
      prefetchEducationOfficeSchoolStat(
        selectSchool.schoolGroupSeq,
        selectSchool.schoolSeq
      );
    } else {
      prefetchEducationOfficeSchoolStat(0, 0);
    }
  }, [selectSchool]);

  useEffect(() => {
    setLoading(true);
    refetchEducationOfficeSchoolStat();
  }, [
    arrowGrade,
    arrowClass,
    arrowMember,
    arrowFinish,
    arrowTotal,
    arrowWrite,
  ]);

  useEffect(() => {
    setLoading(true);
    refetchEducationOfficeSchoolStat();
  }, [rangeDate]);

  useEffect(() => {
    setLoading(true);
    refetchEducationOfficeSchoolStat();
  }, [page]);

  useEffect(() => {
    if (schoolClassInfo.schoolSeq !== 0) {
      setSelectSchool({
        schoolSeq: 0,
      });
      refetchEducationOfficeSchoolStat();
    }
  }, [schoolClassInfo]);

  useEffect(() => {
    setSchoolClassInfo(prev => ({
      ...prev,
      educationOfficeSeq: loginVal.value.user.educationOfficeSeq,
      schoolGroupSeq: loginVal.value.user.schoolGroupSeq,
      schoolSeq: loginVal.value.user.schoolSeq,
      schoolGrade: loginVal.value.user.schoolGrade,
      schoolClass: loginVal.value.user.schoolClass,
    }));
  }, [loginVal]);

  useEffect(() => {
    if (schoolClassInfo.schoolSeq !== 0) {
      refetchEducationOfficeSchoolStat();
    }
  }, []);

  return (
    <>
      <Paper sx={{ p: '1.5rem' }}>
        <SwEducationOfficeStatTab
          tabValue={0}
          educationOfficeInfo={educationOfficeInfo}
          setEducationOfficeInfo={setEducationOfficeInfo}
          showEducationOffice={false}
        />
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-end',
            ml: '.5rem',
            gap: 2,
          }}
        >
          <SwSchoolSelector
            educationOfficeSeq={
              loginVal.value.user.roleName === '마스터'
                ? 0
                : schoolClassInfo.educationOfficeSeq
            }
            selectSchool={selectSchool}
            setSelectSchool={setSelectSchool}
          />
          <Box>
            <SwDateRangePicker
              rangeDate={rangeDate}
              setRangeDate={setRangeDate}
              minStartDate={startOfYear(new Date())}
              maxEndDate={new Date()}
            />
          </Box>
        </Box>
        <Box className='flex_between'>
          <Box>
            <Typography className='title'>
              학급 수 <span style={{ color: '#2581FF' }}>{totalRecords}</span>
            </Typography>
          </Box>
          <Box>
            <Button
              sx={{ mr: '1rem' }}
              color='info'
              variant='outlined'
              onClick={() => {
                downloadClassStatUserList();
              }}
            >
              다운로드
              <FileDownloadRoundedIcon color='success' />
            </Button>
          </Box>
        </Box>
        <Paper sx={{ p: '1rem' }}>
          <TableContainer sx={{ height: '55vh', overflowY: 'scroll' }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  {columns.map((column: any, idx: number) => (
                    <TableCell key={idx.toString()} sx={{ p: '.4rem .7rem' }}>
                      {column}
                      <Button
                        sx={{ padding: '0 .1rem', minWidth: '0' }}
                        onClick={() => onClickArrowButton(idx)}
                      >
                        {' '}
                        {(idx === 2 && arrowMember !== 1) ||
                        (idx === 3 && arrowFinish !== 1) ||
                        (idx === 4 && arrowTotal !== 1) ||
                        (idx === 5 && arrowWrite !== 1) ? (
                          <ArrowDropDownIcon sx={{ fontSize: '2rem' }} />
                        ) : (idx === 2 && arrowMember === 1) ||
                          (idx === 3 && arrowFinish === 1) ||
                          (idx === 4 && arrowTotal === 1) ||
                          (idx === 5 && arrowWrite === 1) ? (
                          <ArrowDropUpIcon sx={{ fontSize: '2rem' }} />
                        ) : (
                          ''
                        )}
                      </Button>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {requestList &&
                  requestList.length > 0 &&
                  requestList
                    .map((row: any) => [
                      row.officeName,
                      row.schoolName,
                      row.memberCount ?? '-',
                      row.finishCount !== null ? row.finishCount : '-',
                      row.totalStep !== null
                        ? commaFormat(`${row.totalStep}`)
                        : '-',
                      row.writeCount !== null ? row.writeCount : '-',
                    ])
                    .map((row: any, idx: number) => (
                      <TableRow key={idx.toString()}>
                        {columns.map((column: any, i: number) => (
                          <TableCell key={i.toString()} sx={{ p: '.7rem' }}>
                            {row[i]}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
              </TableBody>
            </Table>
          </TableContainer>
          <SwPagination
            page={page}
            handleChangePage={handleChangePage}
            count={Math.ceil(totalRecords / rowsPerPage)}
          />
        </Paper>
      </Paper>
      <SwSnackbar
        open={openSnackbar}
        onClose={() => setOpenSnackbar(false)}
        contents={dialogMessage}
      />
      <SwUserStatDetail
        memberSeq={selectedSeq}
        open={openUserStat}
        onClose={() => setOpenUserStat(false)}
      />
      {loading && <SwLoading />}
    </>
  );
};
